import type { KnowledgeCard } from '@/models/knowledge-card.model';

/**
 * Single source of truth for document-type badge colors.
 * Imported by AssetLibraryPage (full KnowledgeCard model) and LinkedDocumentList (DTO source_type).
 */

export const EXTENSION_COLORS: Record<string, string> = {
  PDF: 'red',
  DOCX: 'geekblue',
  XLSX: 'green',
  PPTX: 'orange',
  CSV: 'cyan',
  MD: 'green',
};

export const SOURCE_TYPE_DEFAULTS: Record<string, { label: string; color: string }> = {
  note: { label: 'NOTE', color: 'blue' },
  document: { label: 'DOCUMENT', color: 'geekblue' },
  file_url: { label: 'FILE', color: 'orange' },
  markdown_import: { label: 'MD', color: 'green' },
};

/**
 * Badge for a full KnowledgeCard model instance.
 *
 * For `document` cards we infer type from `sourceUrl` (SharePoint webUrl) because the backend
 * currently returns a single `source_type=document` for both Word and PowerPoint uploads.
 * If we cannot reliably infer an extension (SharePoint viewer routes, redirections, etc.),
 * we display a neutral `DOCUMENT` badge rather than mislabeling as `DOCX`.
 */
export const getTypeBadge = (card: KnowledgeCard): { label: string; color: string } => {
  if (card.sourceType === 'document') {
    const ext = card.getFileExtension();
    if (ext && EXTENSION_COLORS[ext]) {
      return { label: ext, color: EXTENSION_COLORS[ext] };
    }
    return SOURCE_TYPE_DEFAULTS.document;
  }
  if (card.sourceType === 'file_url') {
    const ext = card.getFileExtension();
    if (ext && EXTENSION_COLORS[ext]) {
      return { label: ext, color: EXTENSION_COLORS[ext] };
    }
  }
  return SOURCE_TYPE_DEFAULTS[card.sourceType] ?? { label: 'FILE', color: 'default' };
};

/** Lightweight badge for DTO shapes that only carry source_type (no getFileExtension). */
export const getTypeBadgeForSourceType = (
  sourceType: string | null | undefined,
): { label: string; color: string } =>
  SOURCE_TYPE_DEFAULTS[sourceType ?? ''] ?? { label: 'FILE', color: 'default' };

/**
 * Mirrors KnowledgeCard.getFileExtension() for use with raw DTO source_url strings.
 *
 * Strategy (same as the model):
 * 1. Prefer a real file extension from the URL path.
 * 2. If the path ends in a viewer route (.aspx, .html), try known SharePoint query params
 *    (id, file, filename, name) and extract from their decoded values.
 * 3. Fallback: scan the full decoded URL string for known extensions.
 */
const ALLOWED_EXTS = new Set(['DOCX', 'PPTX', 'PDF', 'XLSX', 'CSV', 'MD']);
const IGNORED_PATH_EXTS = new Set(['ASPX', 'HTML', 'HTM']);

const extFromPath = (value: string): string | null => {
  const match = /\.([a-zA-Z0-9]+)$/.exec(value);
  if (!match) {
    return null;
  }
  const ext = match[1].toUpperCase();
  return ALLOWED_EXTS.has(ext) ? ext : null;
};

const extFromString = (value: string): string | null => {
  const match = /\.(docx|pptx|pdf|xlsx|csv|md)(?:\b|%|&|\?|$)/i.exec(value);
  return match ? match[1].toUpperCase() : null;
};

const extractExtensionFromUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url);

    const pathExtMatch = /\.([a-zA-Z0-9]+)$/.exec(parsed.pathname);
    if (pathExtMatch) {
      const ext = pathExtMatch[1].toUpperCase();
      if (!IGNORED_PATH_EXTS.has(ext) && ALLOWED_EXTS.has(ext)) {
        return ext;
      }
    }

    for (const key of ['id', 'file', 'filename', 'name']) {
      const raw = parsed.searchParams.get(key);
      if (!raw) {
        continue;
      }
      const decoded = decodeURIComponent(raw);
      const extracted = extFromPath(decoded) ?? extFromString(decoded);
      if (extracted) {
        return extracted;
      }
    }

    return extFromString(decodeURIComponent(url));
  } catch {
    return extFromString(url);
  }
};

/**
 * Badge for DTO shapes that carry both source_type and source_url.
 * Infers DOCX / PPTX / XLSX / PDF etc. from the URL when the source_type is
 * 'document' or 'file_url'; falls back to the source_type default otherwise.
 */
export const getTypeBadgeForDTO = (
  sourceType: string | null | undefined,
  sourceUrl: string | null | undefined,
): { label: string; color: string } => {
  if ((sourceType === 'document' || sourceType === 'file_url') && sourceUrl) {
    const ext = extractExtensionFromUrl(sourceUrl);
    if (ext && EXTENSION_COLORS[ext]) {
      return { label: ext, color: EXTENSION_COLORS[ext] };
    }
  }
  return SOURCE_TYPE_DEFAULTS[sourceType ?? ''] ?? { label: 'FILE', color: 'default' };
};
